import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Eye, EyeOff, Lock } from 'lucide-react';
import { formatError } from '../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function ChangePasswordDialog({ open, onOpenChange }) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showNext, setShowNext] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const reset = () => { setCurrent(''); setNext(''); setConfirm(''); setShowNext(false); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!current || !next || !confirm) { toast.error('Tüm alanları doldurun'); return; }
    if (next.length < 6) { toast.error('Yeni şifre en az 6 karakter olmalı'); return; }
    if (next !== confirm) { toast.error('Yeni şifre ve tekrarı eşleşmiyor'); return; }
    if (next === current) { toast.error('Yeni şifre mevcut şifreyle aynı olamaz'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: current, new_password: next,
      }, { withCredentials: true });
      toast.success('Şifre başarıyla değiştirildi');
      reset();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="sm:max-w-md" data-testid="change-password-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <Lock className="w-5 h-5 text-emerald-600" /> Şifre Değiştir
          </DialogTitle>
          <DialogDescription>Hesap güvenliği için en az 6 karakterli yeni bir şifre seç.</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-2">
          <div>
            <Label htmlFor="cp-current">Mevcut Şifre</Label>
            <Input id="cp-current" type="password" value={current} onChange={e => setCurrent(e.target.value)}
              className="mt-1" autoComplete="current-password" data-testid="cp-current-input" />
          </div>
          <div>
            <Label htmlFor="cp-new">Yeni Şifre</Label>
            <div className="relative mt-1">
              <Input id="cp-new" type={showNext ? 'text' : 'password'} value={next} onChange={e => setNext(e.target.value)}
                autoComplete="new-password" data-testid="cp-new-input" className="pr-10" />
              <button type="button" onClick={() => setShowNext(v => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" tabIndex={-1}>
                {showNext ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <p className="text-xs text-slate-400 mt-1">En az 6 karakter</p>
          </div>
          <div>
            <Label htmlFor="cp-confirm">Yeni Şifre (Tekrar)</Label>
            <Input id="cp-confirm" type="password" value={confirm} onChange={e => setConfirm(e.target.value)}
              className="mt-1" autoComplete="new-password" data-testid="cp-confirm-input" />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="cp-cancel-btn">İptal</Button>
            <Button type="submit" disabled={submitting} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="cp-submit-btn">
              {submitting ? 'Güncelleniyor...' : 'Şifreyi Değiştir'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
